import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { FileCheck2, Download, Search } from "lucide-react";
import { toast } from "sonner";

export default function AdminOwnerDeclarationsPanel() {
  const [data, setData] = useState({ total: 0, agreed: 0, pending: 0, rows: [] });
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    const url = filter === "all" ? "/admin/owners/declarations" : `/admin/owners/declarations?status=${filter}`;
    api.get(url).then((r) => setData(r.data)).catch(() => {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("hn_token") || "";
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/owners/declarations.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `thehostelnow-owner-declarations-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded CSV");
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  const filtered = (data.rows || []).filter((r) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (r.name || "").toLowerCase().includes(s) ||
           (r.email || "").toLowerCase().includes(s) ||
           (r.phone || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="owner-declarations-panel" className="space-y-4">
      <div className="rounded-2xl border-2 border-marigold-300 bg-gradient-to-br from-marigold-50 to-white p-4 flex flex-wrap items-center gap-3">
        <div className="grid place-items-center w-10 h-10 rounded-full bg-vermilion-600 text-white shadow-sm">
          <FileCheck2 size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold">Property Owner Declaration audit</div>
          <div className="text-[11px] text-slate-600">
            Total owners: <b>{data.total}</b> · <span className="text-emerald-700">Agreed: <b>{data.agreed}</b></span> · <span className="text-amber-700">Pending: <b>{data.pending}</b></span>
          </div>
        </div>
        <Button data-testid="declarations-export-csv-btn" onClick={exportCsv} className="gap-2 bg-vermilion-600 hover:bg-vermilion-700 text-white">
          <Download size={14} /> Export CSV
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {[
          { v: "all", l: "All owners" },
          { v: "agreed", l: "Agreed" },
          { v: "pending", l: "Pending" },
        ].map((o) => (
          <button
            key={o.v}
            data-testid={`declarations-filter-${o.v}`}
            onClick={() => setFilter(o.v)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 ${filter === o.v ? "border-vermilion-600 bg-vermilion-50 text-vermilion-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
          >
            {o.l}
          </button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <Input
            data-testid="declarations-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            className="pl-8 h-9 rounded-full"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
              <th className="py-3 px-4">Owner</th>
              <th className="py-3 px-4">Contact</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Agreed at</th>
              <th className="py-3 px-4">Agreed from IP</th>
              <th className="py-3 px-4">Version</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500">No owners match this filter.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`declaration-row-${r.id}`} className="border-t border-slate-100">
                <td className="py-3 px-4">
                  <div className="font-semibold">{r.name || "—"}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{r.id.slice(0, 12)}…</div>
                </td>
                <td className="py-3 px-4">
                  <div className="text-xs">{r.email || "—"}</div>
                  <div className="text-[11px] text-slate-500">{r.phone || "—"}</div>
                </td>
                <td className="py-3 px-4">
                  {r.declaration_status === "agreed" ? (
                    <Badge className="bg-emerald-600 text-white">Agreed</Badge>
                  ) : (
                    <Badge className="bg-amber-500 text-white">Pending</Badge>
                  )}
                </td>
                <td className="py-3 px-4 text-xs">{r.declaration_agreed_at ? new Date(r.declaration_agreed_at).toLocaleString("en-IN") : <span className="text-slate-400">—</span>}</td>
                <td className="py-3 px-4 text-xs font-mono">{r.declaration_agreed_ip || <span className="text-slate-400">— not captured</span>}</td>
                <td className="py-3 px-4 text-xs text-slate-500">{r.declaration_version || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-500">
        Owners who signed up before Aug 2026 don't have an IP recorded (declaration was captured but the audit field was added later).
      </div>
    </div>
  );
}
